import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { COUNTERPARTY_ACTION, DIRECTION } from 'components/constants';
import i18n from 'i18n';
import {
  changeCounterpartyActionRequest,
  exportCounterpartiesListCsvRequest,
  exportCounterpartiesMonitoringReportCsvRequest,
  getCounterpartiesListRequest,
  getCustomerCounterpartiesListRequest
} from 'services/requestAgent';
import { convertDate, convertToFullDate } from '../services/utils';
import moment from 'moment';

class CounterpartiesStore {
  isInitialized = false;
  isLoading = false;
  error = null;
  counterpartiesList = [];
  totalElements = null;
  sortColumn = {
    sort_column: 'createdAt',
    sort_direction: DIRECTION.DESC
  };
  pagination = {
    size: 20,
    page: 0,
    totalPages: null
  };
  filters = {
    search_text: null,
    action: Object.keys(COUNTERPARTY_ACTION),
    show_internal: false
  };

  customerCounterpartiesList = [];
  customerCounterpartiesFilters = {
    from_date: moment().subtract(1, 'month').toDate(),
    to_date: moment().toDate(),
    direction: [],
    show_internal: false
  };
  customerCounterpartiesSortColumn = {
    sort_column: 'totalTransactionsAmount',
    sort_direction: DIRECTION.DESC
  };

  constructor() {
    makeAutoObservable(this);
  }

  resetCounterpartiesStore = () => {
    this.isInitialized = false;
    this.isLoading = false;
    this.error = null;
    this.counterpartiesList = [];
    this.totalElements = null;
    this.sortColumn = {
      sort_column: 'createdAt',
      sort_direction: DIRECTION.DESC
    };
    this.pagination = {
      size: 20,
      page: 0,
      totalPages: null
    };
    this.filters = {
      search_text: null,
      action: Object.keys(COUNTERPARTY_ACTION),
      show_internal: false
    };
    this.customerCounterpartiesList = [];
    this.customerCounterpartiesFilters = {
      from_date: moment().subtract(1, 'month').toDate(),
      to_date: moment().toDate(),
      direction: [],
      show_internal: false
    };
    this.customerCounterpartiesSortColumn = {
      sort_column: 'totalTransactionsAmount',
      sort_direction: DIRECTION.DESC
    };
  };

  setIsLoading = (status) => {
    this.isLoading = status;
    this.error = null;
  };

  setPageNumber = (page) => {
    this.pagination.page = page;
  };

  setPageSize = (size) => {
    this.pagination.size = size;
  };

  setFilter = (fieldName, value) => {
    this.filters[fieldName] = value;
    this.pagination.page = 0;
  };

  setCustomerCounterpartiesFilter = (fieldName, value) => {
    this.customerCounterpartiesFilters[fieldName] = value;
    this.pagination.page = 0;
  };

  setSortData = (sortData) => {
    this.sortColumn.sort_column = sortData.sortBy;
    this.sortColumn.sort_direction = sortData.direction;
  };

  setFiltersFromUrl = (params) => {
    const filters = { ...this.filters };
    const pagination = { ...this.pagination };

    // Mapping URL filter parameters to corresponding properties in the 'filters' object
    const filterParamsMapping = {
      search_text: 'search_text',
      statuses: 'statuses',
      show_internal: 'show_internal'
    };

    // Iterating over each URL parameter and assigning its values to 'filters'
    for (const param in params) {
      if (param in filterParamsMapping) {
        let value = params[param];

        if (value === 'true') {
          value = true;
        } else if (value === 'false') {
          value = false;
        }

        // Handle only array values as arrays, keep others as their original types
        if (Array.isArray(value)) {
          filters[filterParamsMapping[param]] = value;
        } else {
          filters[filterParamsMapping[param]] = value;
        }
      }
    }

    // Assigning pagination parameters from the URL
    pagination.page = parseInt(params.page) || pagination.page;
    pagination.size = parseInt(params.size) || pagination.size;

    // Assigning sorting parameters from the URL
    this.sortColumn.sort_column = params.sort_column || this.sortColumn.sort_column;
    this.sortColumn.sort_direction = params.sort_direction || this.sortColumn.sort_direction;

    // Updating the state of filters and pagination
    this.filters = filters;
    this.pagination = pagination;
  };

  prepareFiltersParams = () => {
    return { ...this.filters };
  };

  getCounterpartiesList = async () => {
    this.setIsLoading(true);
    try {
      const res = await getCounterpartiesListRequest(
        this.pagination,
        this.sortColumn.sort_column,
        this.sortColumn.sort_direction,
        this.filters.search_text,
        this.filters
      );

      runInAction(() => {
        this.isInitialized = true;
        this.counterpartiesList = res.content;
        this.totalElements = res.total_elements;
        this.pagination = {
          size: res.size,
          page: res.number,
          totalPages: res.total_pages
        };
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  changeCounterpartyAction = async (counterpartyId, data) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(changeCounterpartyActionRequest(counterpartyId, data), {
      pending: i18n.getMessage('counterpartiesStore.changeCounterpartyAction.toastPromise.pending'),
      success: i18n.getMessage('counterpartiesStore.changeCounterpartyAction.toastPromise.success')
    });

    try {
      const counterpartyDetails = await toastPromise;

      const updatedCounterpartiesList = this.counterpartiesList.map((counterparty) =>
        counterparty.id === counterpartyId ? counterpartyDetails : counterparty
      );

      runInAction(() => {
        this.counterpartiesList = updatedCounterpartiesList;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  exportCounterpartiesListCsv = async () => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(exportCounterpartiesListCsvRequest(), {
      pending: i18n.getMessage('counterpartiesStore.exportCounterpartiesListCsv.toastPromise.pending'),
      success: i18n.getMessage('counterpartiesStore.exportCounterpartiesListCsv.toastPromise.success')
    });

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  setCustomerCounterpartiesFiltersFromUrl = (params) => {
    const filters = { ...this.customerCounterpartiesFilters };
    const pagination = { ...this.pagination };

    // Mapping URL filter parameters to corresponding properties in the 'filters' object
    const filterParamsMapping = {
      from_date: 'from_date',
      to_date: 'to_date',
      direction: 'direction',
      show_internal: 'show_internal'
    };

    // Iterating over each URL parameter and assigning its values to 'filters'
    for (const param in params) {
      if (param in filterParamsMapping) {
        let value = params[param];

        // Convert `from_date` and `to_date` if they exist
        if (param === 'from_date' || param === 'to_date') {
          value = convertToFullDate(value);
        }

        if (value === 'true') {
          value = true;
        } else if (value === 'false') {
          value = false;
        }

        // Handle only array values as arrays, keep others as their original types
        if (Array.isArray(value)) {
          filters[filterParamsMapping[param]] = value;
        } else {
          filters[filterParamsMapping[param]] = value;
        }
      }
    }

    // Assigning pagination parameters from the URL
    pagination.page = parseInt(params.page) || pagination.page;
    pagination.size = parseInt(params.size) || pagination.size;

    // Assigning sorting parameters from the URL
    this.sortColumn.sort_column = params.sort_column || this.sortColumn.sort_column;
    this.sortColumn.sort_direction = params.sort_direction || this.sortColumn.sort_direction;

    // Updating the state of filters and pagination
    this.customerCounterpartiesFilters = filters;
    this.pagination = pagination;
  };

  prepareCustomerCounterpartiesFiltersParams = () => {
    const params = { ...this.customerCounterpartiesFilters };

    if (this.customerCounterpartiesFilters.from_date) {
      params.from_date = convertDate(this.customerCounterpartiesFilters.from_date);
    } else {
      delete params.from_date;
    }

    if (this.customerCounterpartiesFilters.to_date) {
      params.to_date = convertDate(this.customerCounterpartiesFilters.to_date);
    } else {
      delete params.to_date;
    }

    return params;
  };

  setCustomerCounterpartiesSortData = (sortData) => {
    this.customerCounterpartiesSortColumn.sort_column = sortData.sortBy;
    this.customerCounterpartiesSortColumn.sort_direction = sortData.direction;
  };

  getCustomerCounterpartiesList = async (customerId) => {
    this.setIsLoading(true);

    const filtersParams = this.prepareCustomerCounterpartiesFiltersParams();

    try {
      const res = await getCustomerCounterpartiesListRequest(
        customerId,
        this.customerCounterpartiesSortColumn.sort_column,
        this.customerCounterpartiesSortColumn.sort_direction,
        filtersParams
      );

      runInAction(() => {
        this.isInitialized = true;
        this.customerCounterpartiesList = res;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  exportCounterpartiesMonitoringReportCsv = async (customerId) => {
    this.setIsLoading(true);

    const filtersParams = this.prepareCustomerCounterpartiesFiltersParams();

    const toastPromise = toast.promise(
      exportCounterpartiesMonitoringReportCsvRequest(
        customerId,
        this.customerCounterpartiesSortColumn.sort_column,
        this.customerCounterpartiesSortColumn.sort_direction,
        filtersParams
      ),
      {
        pending: i18n.getMessage('counterpartiesStore.exportCounterpartiesMonitoringReportCsv.toastPromise.pending'),
        success: i18n.getMessage('counterpartiesStore.exportCounterpartiesMonitoringReportCsv.toastPromise.success')
      }
    );

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };
}

export default new CounterpartiesStore();
